import { createPortal } from "react-dom";
import "./Full_traceback_log.css";
import { useEffect, useMemo, useState } from "react";

export function Full_traceback_log({ setShowModal, errorCode }) {
  const [logs, setLogs] = useState([]);
  const [selectedIssue, setSelectedIssue] = useState(null);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const API_URL = process.env.REACT_APP_API_FAST || "http://192.168.1.103:8000";

  useEffect(() => {
    if (!errorCode) return;

    const fetchLogs = async () => {
      setLoading(true);
      try {
        const res = await fetch(`${API_URL}/api/get_traceback`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ error_code: errorCode }),
        });

        const data = await res.json();
        const tracebacks = data.full_traceback || [];

        setLogs(tracebacks);

        if (tracebacks.length) {
          setSelectedIssue({
            error_code: tracebacks[0].error_code,
            error_type: tracebacks[0].error_type,
            count: tracebacks.length,
            logs: tracebacks,
            latest: tracebacks[0],
          });
        }
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };

    fetchLogs();
  }, [errorCode]);

  const issues = useMemo(() => {
    const map = new Map();
    logs.forEach((log) => {
      if (!map.has(log.error_code)) {
        map.set(log.error_code, {
          error_code: log.error_code,
          error_type: log.error_type,
          count: 0,
          logs: [],
          latest: log,
        });
      }
      const issue = map.get(log.error_code);
      issue.count += 1;
      issue.logs.push(log);
      issue.latest = log;
    });
    return Array.from(map.values());
  }, [logs]);

  const filteredIssues = useMemo(() => {
    return issues.filter((i) =>
      `${i.error_code} ${i.error_type}`
        .toLowerCase()
        .includes(search.toLowerCase())
    );
  }, [issues, search]);

  const debugData = useMemo(() => {
    try {
      return selectedIssue?.latest?.debug_data
        ? JSON.parse(selectedIssue.latest.debug_data)
        : null;
    } catch {
      return null;
    }
  }, [selectedIssue]);

  const parseFrames = (debugData) => {
    if (!debugData?.parsed_st) return [];
    try {
      let raw = debugData.parsed_st;
      if (typeof raw === "string") {
        raw = raw
          .replace(/'/g, '"')
          .replace(/None/g, "null")
          .replace(/True/g, "true")
          .replace(/False/g, "false");
        return JSON.parse(raw).frames || [];
      }
      return raw.frames || [];
    } catch {
      return [];
    }
  };

  const parsedFrames = useMemo(() => parseFrames(debugData), [debugData]);

  const openVSCode = (file, line = 1) => {
    if (!file) return;
    const normalized = file.replace(/\\/g, "/");
    window.location.href = `vscode://file/${normalized}:${line}`;
  };

  const renderFile = (file, line) => (
    <button className="file_link" onClick={() => openVSCode(file, line)}>
      {file}
    </button>
  );

  // ====================== IMPROVED JSON VIEWER ======================
  const isFilePath = (value) => {
    if (typeof value !== "string") return false;
    const t = value.trim();
    return (
      /^[a-zA-Z]:[\\/]/.test(t) ||
      t.startsWith("/") ||
      (/[\\/]/.test(t) && /\.(py|js|ts|jsx|tsx)$/.test(t))
    );
  };

  const JsonViewer = ({ data, onOpenVSCode }) => {
    if (data === null || data === undefined)
      return <span className="json_value">null</span>;

    if (typeof data !== "object") {
      return isFilePath(data) ? (
        <div className="json_file_path">
          <span className="file_path_text">{data}</span>
          <button className="mini_vscode" onClick={() => onOpenVSCode(data, 1)}>
            Open VS Code
          </button>
        </div>
      ) : (
        <span className="json_value">{String(data)}</span>
      );
    }

    // Special handling for stack frame objects
    if (!Array.isArray(data) && data.file) {
      return (
        <div className="json_block frame_object">
          {"{"}
          {Object.entries(data).map(([key, value]) => (
            <div key={key} className="json_row">
              <div className="json_key">{key}:</div>
              <div className="json_value_wrap">
                {key === "file" && isFilePath(value) ? (
                  <div className="json_file_path">
                    <span className="file_path_text">{value}</span>
                    <button
                      className="mini_vscode"
                      onClick={() => onOpenVSCode(value, data.line || 1)}
                    >
                      Open VS Code
                    </button>
                  </div>
                ) : (
                  <JsonViewer data={value} onOpenVSCode={onOpenVSCode} />
                )}
              </div>
            </div>
          ))}
          {"}"}
        </div>
      );
    }

    if (Array.isArray(data)) {
      return (
        <div className="json_block">
          [
          {data.map((item, i) => (
            <div key={i} className="json_child">
              <JsonViewer data={item} onOpenVSCode={onOpenVSCode} />
            </div>
          ))}
          ]
        </div>
      );
    }

    // Normal objects
    return (
      <div className="json_block">
        {"{"}
        {Object.entries(data).map(([key, value]) => (
          <div key={key} className="json_row">
            <div className="json_key">{key}:</div>
            <div className="json_value_wrap">
              <JsonViewer data={value} onOpenVSCode={onOpenVSCode} />
            </div>
          </div>
        ))}
        {"}"}
      </div>
    );
  };

  return createPortal(
    <>
      <div className="backdrop" />

      <div className="monitor_container">
        {/* SIDEBAR */}
        <div className="sidebar">
          <div className="sidebar_header">
            <h3>Monitoring</h3>
            <input
              className="search"
              placeholder="Search issues..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          {loading && <div className="loading">Loading...</div>}

          <div className="issue_list">
            {filteredIssues.map((issue) => (
              <div
                key={issue.error_code}
                className={`issue_card ${
                  selectedIssue?.error_code === issue.error_code ? "active" : ""
                }`}
                onClick={() => setSelectedIssue(issue)}
              >
                <div className="issue_top">
                  <span className="code">{issue.error_code}</span>
                  <span className="count">{issue.count}x</span>
                </div>
                <div className="type">{issue.error_type}</div>
                <div className="severity high">HIGH</div>
              </div>
            ))}
          </div>

          <button className="close" onClick={() => setShowModal(false)}>
            Close
          </button>
        </div>

        {/* DETAIL */}
        <div className="detail">
          {!selectedIssue ? (
            <div>Select an issue</div>
          ) : (
            <>
              <div className="header">
                <div>
                  <h2>{selectedIssue.error_type}</h2>
                  <span className="badge">{selectedIssue.error_code}</span>
                </div>
                <button
                  className="vscode_btn"
                  onClick={() =>
                    openVSCode(
                      selectedIssue.latest.file_path,
                      selectedIssue.latest.line_number
                    )
                  }
                >
                  Open VS Code
                </button>
              </div>

              <div className="panel critical_panel">
                <div className="critical_header">
                  <span className="critical_badge">CRITICAL</span>
                </div>
                <pre className="critical_error">
                  {selectedIssue.latest.error_message}
                </pre>
              </div>

              <div className="panel">
                <h4>Summary</h4>
                <div className="summary_grid">
                  <div>Error Code: {selectedIssue.latest.error_code}</div>
                  <div>Queue: {selectedIssue.latest.queue_name}</div>
                  <div>Function: {selectedIssue.latest.function_name}</div>
                  <div>
                    File:{" "}
                    {renderFile(
                      selectedIssue.latest.file_path,
                      selectedIssue.latest.line_number
                    )}
                  </div>
                  <div>Line: {selectedIssue.latest.line_number}</div>
                  <div>Count: {selectedIssue.count}</div>
                </div>
              </div>

              <div className="panel">
                <h4>Source Code</h4>
                <pre className="error_box">
                  {selectedIssue.latest.source_code}
                </pre>
              </div>

              {parsedFrames.length > 0 && (
                <div className="panel">
                  <h4>Stack Trace</h4>
                  {parsedFrames.map((f, i) => (
                    <div key={i} className="trace_frame">
                      <div className="trace_top">
                        <div className="trace_function">{f.function}</div>
                        <button
                          className="frame_vscode"
                          onClick={() => openVSCode(f.file, f.line)}
                        >
                          VS Code
                        </button>
                      </div>
                      {renderFile(f.file, f.line)}
                      <div className="trace_line">Line {f.line}</div>
                    </div>
                  ))}
                </div>
              )}

              {/* DEBUG JSON */}
              {/* {debugData && (
                <div className="panel">
                  <h4>Debug (Interactive JSON)</h4>
                  <JsonViewer data={debugData} onOpenVSCode={openVSCode} />
                </div>
              )} */}
            </>
          )}
        </div>
      </div>
    </>,
    document.body
  );
}
